import * as React from 'react'
import { Col, Modal } from 'antd'
import { colors } from 'web-makeup/dist/design-system/config/data/theme'
import { OptionalFlex } from 'web-makeup/dist/styles/layout'
import { useMutation, useQuery } from '@apollo/react-hooks'
import Skeleton from '@yisheng90/react-loading'
import { getCurrentUser, isAdmin, isManager, isProducer } from 'web-fns/dist/auth'
import { useFlags } from 'launchdarkly-react-client-sdk'

import SearchSection from '../../components/SearchSection/search-section'
import { Card } from '../../components/TeamCard/card'
import DefaultButton from '../../components/DefaultButton/DefaultButton'

import EnvironmentContext from '../../../EnvironmentContext'
import { client as apolloClient } from '../../../ApolloProvider'

import { GET_BRAND_TEAM } from '../../../../queries/GetBrandTeam'
import { User } from '../../../../typings/types'
import { ADD_TEAM_MEMBER } from '../../../../mutations/AddTeamMember'
import { REMOVE_TEAM_MEMBER } from '../../../../mutations/RemoveTeamMember'
import { UPDATE_USER } from '../../../../mutations/UpdateUser'
import { UPDATE_TEAM_MEMBER } from '../../../../mutations/UpdateTeamMember'
import LocationForm from '../../../../components/LocationForm/LocationForm'

import {
  ButtonWrapper,
  FooterLink,
  FormError,
  Input,
  InputLabel,
  OrdersHeader,
  StyledSelect,
} from '../../BrandDashboardSettings/BrandDashboardSettings.style'
import { PositionDefaultRole, PositionSecurityLevel } from '../../enums/brand-dashboard'
import PopUp from '../../components/PopUp/PopUp'

import {
  Wrapper,
  Content,
  ModalTitle,
  ModalContent,
  AntModalStyles,
  StyledRow,
  StyledSectionTitle,
  TeamWrapper,
} from './brand-team.style'

interface BrandDashboardSettingsWithDataProps {
  brandOrderUuid?: string
  urlId?: string
}

const BrandTeam: React.FC<BrandDashboardSettingsWithDataProps> = ({ brandOrderUuid, urlId }) => {
  const [searchTerm, setSearchTerm] = React.useState('')
  const [teamMemberModal, setTeamMemberModal] = React.useState(false)
  const [isEdit, setIsEdit] = React.useState(false)
  const [isPopUpActive, setIsPopUpActive] = React.useState(false)
  const [currentDeleteId, setCurrentDeleteId] = React.useState('')
  const [isEmailValid, setIsEmailValid] = React.useState(false)
  const [isPhoneValid, setIsPhoneValid] = React.useState(true)
  const [isFormSubmitted, setIsFormSubmitted] = React.useState(false)
  const [cityUuid, setCityUuid] = React.useState('')
  const [countryUuid, setCountryUuid] = React.useState('')
  const [cityName, setCityName] = React.useState('')
  const [countryName, setCountryName] = React.useState('')
  const [tMIndex, setTMIndex] = React.useState<number>()
  const [restrictedMembers, setRestrictedMembers] = React.useState<any>([])
  const [unrestrictedMembers, setUnrestrictedMembers] = React.useState<any>([])

  const [teamMemberData, setTeamMemberData] = React.useState({
    id: '',
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    role: '',
    securityLevel: '',
    position: '',
  })

  const flags = useFlags()
  const brandDashboardEdit = flags['brand-dashboard-edit-team-member']
  const { GEN1_BASE_URL } = React.useContext(EnvironmentContext) || ''

  const brandId = urlId ? urlId : brandOrderUuid
  const { Option } = StyledSelect

  const handleSearch = e => {
    return setSearchTerm(e.target.value)
  }

  const [addTeamMember, { loading: addTMLoading }] = useMutation(ADD_TEAM_MEMBER, {
    client: apolloClient,
  })
  const [removeTeamMember, { loading: removeTMLoading, data: removedTMdata }] = useMutation(
    REMOVE_TEAM_MEMBER,
    {
      client: apolloClient,
    }
  )
  const [updateUser, { loading: updateUserLoading }] = useMutation(UPDATE_USER, {
    client: apolloClient,
  })
  const [updateTM, { loading: updateTMLoading }] = useMutation(UPDATE_TEAM_MEMBER, {
    client: apolloClient,
  })

  const refetchTeamMebers = () => {
    refetchTeam({
      brandUuid: brandId,
      isBrand,
    })
  }

  const updateTeamMember = userUuid => {
    const userUpdate = updateUser({
      variables: {
        userUuid,
        user: {
          firstName: teamMemberData.firstName,
          lastName: teamMemberData.lastName,
          email: teamMemberData.email,
          phoneNumber: teamMemberData.phoneNumber,
          countryUuid,
          cityUuid,
        },
      },
    })
    const TMUpdate = updateTM({
      variables: {
        userUuid,
        brandUuid: brandId,
        position: {
          title: teamMemberData.position,
          defaultRole: teamMemberData.role,
          securityLevel: teamMemberData.securityLevel,
        },
      },
    })
    return { userUpdate, TMUpdate }
  }

  React.useEffect(
    () => {
      if (
        removedTMdata &&
        removedTMdata.removeTeamMember &&
        removedTMdata.removeTeamMember.success
      ) {
        refetchTeamMebers()
      }
    },
    [removedTMdata]
  )

  const isBrand = !(isAdmin() || isProducer() || isManager()) || false

  const { data: getBrandTeamData, refetch: refetchTeam, loading: TMLoading } = useQuery(
    GET_BRAND_TEAM,
    {
      variables: { brandUuid: brandId, isBrand },
      client: apolloClient,
    }
  )

  const validateEmail = email => {
    if (/[\s\S]+@([\w-]+\.)+[\w-]{2,10}$/g.test(email)) {
      return setIsEmailValid(true)
    }
    return setIsEmailValid(false)
  }

  const { getBrandTeam } = getBrandTeamData || '[]'

  React.useEffect(
    () => {
      setRestrictedMembers(
        getBrandTeam &&
          getBrandTeam.filter(
            e => e && e.securityLevel === PositionSecurityLevel.RESTRICTED.toUpperCase()
          )
      )
      setUnrestrictedMembers(
        getBrandTeam &&
          getBrandTeam.filter(
            e => e && e.securityLevel !== PositionSecurityLevel.RESTRICTED.toUpperCase()
          )
      )
    },
    [getBrandTeam]
  )

  const isButtonDisabled =
    teamMemberData.firstName === '' ||
    teamMemberData.email === '' ||
    teamMemberData.lastName === '' ||
    teamMemberData.role === '' ||
    teamMemberData.securityLevel === ''

  const ValidatePhone = phone => {
    if (/^\+?(?:[\d]*)$/g.test(phone) || phone === null) {
      return setIsPhoneValid(true)
    }
    return setIsPhoneValid(false)
  }

  React.useEffect(() => {
    document.getElementsByClassName('main-content')[0].classList.add('team')
    document.body.className = ''
    document.body.classList.add('team')
    return () => {
      document.getElementsByClassName('main-content')[0].classList.remove('team')
      document.body.className = ''
      document.body.classList.add('team')
    }
  }, [])

  const currentUser = getCurrentUser()
  const currentUserUuid = currentUser && currentUser.uuid

  const teamMemberModalComponent = (
    <Modal
      visible={teamMemberModal}
      onCancel={() => setTeamMemberModal(false)}
      width="53rem"
      footer={[
        <ButtonWrapper key="1">
          {isEdit && (
            <FooterLink target="_blank" href={`${GEN1_BASE_URL}/humans/${teamMemberData.id}/edit`}>
              View full profile
            </FooterLink>
          )}
          <DefaultButton
            fontSize="1rem"
            padding="0 1.625rem"
            backgroundColor={colors.adaptBlue}
            style={{ marginLeft: 'auto' }}
            disabled={isButtonDisabled}
            onClick={async () => {
              setIsFormSubmitted(true)
              if (isEmailValid && isPhoneValid) {
                setTeamMemberModal(false)
                isEdit
                  ? await updateTeamMember(teamMemberData.id)
                  : await addTeamMember({
                      variables: {
                        brandUuid: brandOrderUuid,
                        teamMember: {
                          email: teamMemberData.email || '',
                          firstName: teamMemberData.firstName || '',
                          lastName: teamMemberData.lastName || '',
                          phoneNumber: teamMemberData.phoneNumber || '',
                        },
                        position: {
                          defaultRole: teamMemberData.role,
                          securityLevel: teamMemberData.securityLevel,
                          title: teamMemberData.position,
                        },
                      },
                    })
                setIsFormSubmitted(false)
                setIsEmailValid(false)
                window.scrollTo({
                  top: 0,
                  behavior: 'smooth',
                })
                refetchTeamMebers()
              }
            }}
          >
            {isEdit ? 'Save changes' : 'Add team member'}
          </DefaultButton>
        </ButtonWrapper>,
      ]}
    >
      <ModalTitle>{isEdit ? 'Edit team member' : 'Add a new team member'}</ModalTitle>
      <ModalContent>
        {isEdit
          ? 'Edit your team member’s information to update their details.'
          : 'Fill your new team member’s information and add them into your team.'}
      </ModalContent>

      <StyledRow type="flex" gutter={24}>
        <Col xs={24} sm={24} md={12} lg={12}>
          <InputLabel>First Name</InputLabel>
          <Input
            type="text"
            placeholder="Add person’s first name"
            className={teamMemberData.firstName !== '' ? 'filled' : ''}
            onChange={e => setTeamMemberData({ ...teamMemberData, firstName: e.target.value })}
            value={teamMemberData.firstName}
          />
        </Col>
        <Col xs={24} sm={24} md={12} lg={12}>
          <InputLabel>Last Name</InputLabel>
          <Input
            type="text"
            placeholder="Add person’s last name"
            className={teamMemberData.lastName !== '' ? 'filled' : ''}
            onChange={e => setTeamMemberData({ ...teamMemberData, lastName: e.target.value })}
            value={teamMemberData.lastName}
          />
        </Col>
      </StyledRow>
      <StyledRow type="flex" gutter={24}>
        <Col xs={24} sm={24} md={12} lg={12}>
          <InputLabel>Email</InputLabel>
          <Input
            type="text"
            placeholder="Add person’s email"
            className={teamMemberData.email !== '' ? 'filled' : ''}
            onChange={e => {
              setTeamMemberData({ ...teamMemberData, email: e.target.value })
              validateEmail(e.target.value)
            }}
            autoComplete="nope"
            value={teamMemberData.email}
          />
          {isFormSubmitted && !isEmailValid && <FormError>Email is not valid.</FormError>}
        </Col>
        <Col xs={24} sm={24} md={12} lg={12}>
          <InputLabel>Phone number</InputLabel>
          <Input
            type="text"
            placeholder="Phone number"
            className={teamMemberData.phoneNumber !== '' ? 'filled' : ''}
            onChange={e => {
              setTeamMemberData({ ...teamMemberData, phoneNumber: e.target.value })
              ValidatePhone(e.target.value)
            }}
            value={teamMemberData.phoneNumber}
          />
          {isFormSubmitted && !isPhoneValid && <FormError>Phone is not valid.</FormError>}
        </Col>
      </StyledRow>
      {isEdit && (
        <LocationForm
          setCountryId={e => {
            setCountryName('')
            setCountryUuid(e)
          }}
          setCityId={e => {
            setCityName('')
            setCityUuid(e)
          }}
          error={false}
          countryDisplayValue={countryName}
          cityDisplayValue={cityName}
        />
      )}
      <StyledRow type="flex" gutter={24}>
        <Col xs={24} sm={24} md={12} lg={12}>
          <InputLabel>Role in the team</InputLabel>
          <StyledSelect
            className={teamMemberData.role !== '' ? 'filled' : ''}
            name="role"
            value={PositionDefaultRole[teamMemberData.role]}
            suffixIcon={
              <img src="https://res.cloudinary.com/ninety-seconds/image/upload/v1632366093/-design-system/icon-arrow-down-black.svg" />
            }
            onChange={e => setTeamMemberData({ ...teamMemberData, role: e })}
          >
            {(Object.keys(PositionDefaultRole) as Array<keyof typeof PositionDefaultRole>).map(
              val => (
                <Option key={val} value={val}>
                  {val.toLowerCase()}
                </Option>
              )
            )}
          </StyledSelect>
        </Col>
        <Col xs={24} sm={24} md={12} lg={12}>
          <InputLabel>Position</InputLabel>
          <Input
            type="text"
            placeholder="Add person’s position"
            className={teamMemberData.position !== '' ? 'filled' : ''}
            onChange={e => setTeamMemberData({ ...teamMemberData, position: e.target.value })}
            value={teamMemberData.position}
          />
        </Col>
      </StyledRow>
      <StyledRow type="flex" gutter={24}>
        <Col xs={24} sm={24} md={24} lg={24}>
          <InputLabel>Security level</InputLabel>
          <StyledSelect
            className={teamMemberData.securityLevel !== '' ? 'filled' : ''}
            name="securityLevel"
            placeholder="Pick person’s role in your 90 Seconds team"
            value={PositionSecurityLevel[teamMemberData.securityLevel]}
            suffixIcon={
              <img src="https://res.cloudinary.com/ninety-seconds/image/upload/v1632366093/-design-system/icon-arrow-down-black.svg" />
            }
            onChange={e => setTeamMemberData({ ...teamMemberData, securityLevel: e })}
          >
            {(Object.keys(PositionSecurityLevel) as Array<keyof typeof PositionSecurityLevel>).map(
              val => (
                <Option key={val} value={val}>
                  {val.toLowerCase()}
                </Option>
              )
            )}
          </StyledSelect>
        </Col>
      </StyledRow>
    </Modal>
  )

  const removeTM = id => {
    removeTeamMember({
      variables: {
        brandUuid: brandOrderUuid,
        userUuid: id,
      },
    })
  }

  const editTMAction = async (
    uuid,
    email,
    firstName,
    lastName,
    defaultRole,
    phoneNumber,
    securityLevel,
    position,
    city,
    country
  ) => {
    !isBrand && brandDashboardEdit && setIsEdit(true)
    setTeamMemberData({
      id: uuid,
      email,
      firstName,
      lastName,
      role: defaultRole,
      phoneNumber,
      securityLevel,
      position,
    })
    setCountryName(country)
    setCityName(city)
    validateEmail(email)
    ValidatePhone(phoneNumber)
    setTeamMemberModal(true)
  }

  const teamMemberSection = teamList => (
    <TeamWrapper>
      {teamList &&
        teamList
          .filter(
            item =>
              searchTerm
                ? item.human && item.human.name.toLowerCase().includes(searchTerm.toLowerCase())
                : item
          )
          .map(
            (item, index) =>
              item && (
                <div key={index} style={{ position: 'relative' }}>
                  {isPopUpActive &&
                    tMIndex === index &&
                    currentDeleteId === item.human.uuid && (
                      <PopUp
                        style={{
                          top: '2.75rem',
                          position: 'absolute',
                          right: '2rem',
                        }}
                        text="Are you sure you want to remove this team member?"
                        onNo={() => setIsPopUpActive(false)}
                        onYes={async () => {
                          await removeTM(currentDeleteId)
                          setIsPopUpActive(false)
                        }}
                      />
                    )}
                  <Card
                    user={item.human as User}
                    role={item.defaultRole as PositionDefaultRole}
                    level={(item.securityLevel as PositionSecurityLevel) || ''}
                    isCurrentUser={item.human.uuid === currentUserUuid}
                    editUser={() => {
                      !brandDashboardEdit
                        ? GEN1_BASE_URL &&
                          window.open(`${GEN1_BASE_URL}/humans/${item.human.uuid}/edit`, '_blank')
                        : editTMAction(
                            item.human.uuid,
                            item.human.email,
                            item.human.firstName,
                            item.human.lastName,
                            item.defaultRole,
                            item.human.phoneNumber,
                            item.securityLevel,
                            item.title,
                            item.human.location.city,
                            item.human.location.country
                          )
                    }}
                    onDeleteIcon={() => {
                      setCurrentDeleteId(item.human.uuid)
                      setTMIndex(index)
                      setIsPopUpActive(true)
                    }}
                  />
                </div>
              )
          )}
    </TeamWrapper>
  )

  return (
    <>
      <AntModalStyles />
      <OrdersHeader>
        <h3 data-testid="header-title">Team</h3>
      </OrdersHeader>
      <Wrapper>
        <Content>
          <SearchSection
            onChange={e => handleSearch(e)}
            onClearInput={() => setSearchTerm('')}
            addMemberButton={() => {
              setIsPhoneValid(true)
              setIsEmailValid(true)
              setTeamMemberModal(true)
              setIsEdit(false)
              setTeamMemberData({
                id: '',
                email: '',
                firstName: '',
                lastName: '',
                role: '',
                phoneNumber: '',
                securityLevel: '',
                position: '',
              })
              setIsEmailValid(false)
            }}
          />
          {updateUserLoading || updateTMLoading || removeTMLoading || addTMLoading || TMLoading ? (
            <OptionalFlex>
              <Skeleton width="40%" height={32} />
              <Skeleton width="14%" height={32} />
              <Skeleton width="14%" height={32} />
            </OptionalFlex>
          ) : (
            <>
              {unrestrictedMembers &&
                unrestrictedMembers.length > 0 && <StyledSectionTitle>My team</StyledSectionTitle>}
              {teamMemberSection(unrestrictedMembers)}
              {restrictedMembers &&
                restrictedMembers.length > 0 && (
                  <StyledSectionTitle>Restricted members</StyledSectionTitle>
                )}
              {teamMemberSection(restrictedMembers)}
            </>
          )}
        </Content>
      </Wrapper>
      {teamMemberModalComponent}
    </>
  )
}

export default BrandTeam



// WEBPACK FOOTER //
// ./src/components/BrandDashboard/BrandDashboardTeam/BrandTeam/brand-team.tsx